// "Find pre-leads": the lead agent in a dialog; the ticked rows become pre-leads to review instead of deals.
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import type {
  RankedLead,
  SegmentsAnswer,
} from "../../../../../supabase/functions/_shared/leadAgent.ts";
import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { LeadAgent } from "../leads/LeadAgent";
import { insertPreLeads, recentRejections } from "./preLeads";

export const FindPreLeadsDialog = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const config = useConfigurationContext();
  const { identity } = useGetIdentity();
  const [open, setOpen] = useState(false);
  const { data: rejections } = useQuery({
    queryKey: ["pre_leads", "rejections"],
    queryFn: () => recentRejections(dataProvider, config),
    enabled: open,
  });

  const onAdd = async (rows: RankedLead[], plan: SegmentsAnswer) => {
    const { added, skipped } = await insertPreLeads(dataProvider, rows, {
      salesId: identity?.id,
      runId: new Date().toISOString(),
      segments: plan.segments,
      offer: plan.offer,
    });
    notify("resources.pre_leads.notify.added", {
      type: "success",
      messageArgs: { added, skipped: skipped.length },
    });
    refresh();
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="max-md:min-h-11">
          <Search />
          {translate("resources.pre_leads.find")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="sr-only">
          {translate("resources.pre_leads.find")}
        </DialogTitle>
        <LeadAgent onAdd={onAdd} rejections={rejections} />
      </DialogContent>
    </Dialog>
  );
};
